import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";

import RegistrationForm from "./components/RegistrationForm";
import { campuses } from "./data/campuses";
import "./App.css";

const App = () => {
  // The form is visible immediately — reCAPTCHA is verified at submit time
  // instead (see RegistrationForm), not as a gate before the parent can even
  // start filling it in. formKey only bumps once the parent explicitly asks
  // for a fresh form (see handleRegisterAnother) — not immediately on
  // submit, since a toast that vanishes in a few seconds isn't a reliable
  // way to confirm something this important actually went through.
  const [formKey, setFormKey] = useState(0);
  const [showContact, setShowContact] = useState(false);

  // Set on a successful submission; showing this (instead of resetting
  // straight back to a blank form) is what actually persists on screen
  // until the parent chooses to register another student.
  const [confirmedRegistrationNumber, setConfirmedRegistrationNumber] =
    useState<string | null>(null);

  // Mirrored from RegistrationForm's own campus field, purely so the footer
  // and contact modal can show that campus's real address — each of the 5
  // campuses is a different physical location, so a single fixed address
  // (like the old single-campus Eon site had) doesn't make sense here.
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const selectedCampus = campuses.find((c) => c._id === selectedCampusId);

  const handleSubmitted = (registrationNumber: string) => {
    setConfirmedRegistrationNumber(registrationNumber);
  };

  const handleRegisterAnother = () => {
    setConfirmedRegistrationNumber(null);
    setFormKey((k) => k + 1);
  };

  useEffect(() => {
    if (!showContact) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowContact(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showContact]);

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-inner container">
          <div className="app-brand">
            <div className="app-logo-badge">
              <img
                src={selectedCampus?.logo || "/mpa-logo.png"}
                alt="Metropolitan Academy"
                className="app-logo"
              />
            </div>
            <div className="app-brand-text">
              <h1 className="app-title">METROPOLITAN ACADEMY</h1>
            </div>
          </div>
          <div className="app-header-actions">
            <button
              type="button"
              className="btn btn-contact"
              onClick={() => setShowContact(true)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              Contact Us
            </button>
          </div>
        </div>
      </header>

      {showContact && (
        <div
          className="contact-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-title"
          onClick={() => setShowContact(false)}
        >
          <div
            className="contact-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="contact-modal-close"
              aria-label="Close"
              onClick={() => setShowContact(false)}
            >
              &times;
            </button>
            <div className="contact-modal-header">
              <img
                src={selectedCampus?.logo || "/mpa-logo.png"}
                alt=""
                className="contact-modal-logo"
                aria-hidden="true"
              />
              <h4 id="contact-title" className="mb-0">
                Contact Metropolitan Academy
              </h4>
              <p className="text-muted small mb-0">
                {selectedCampus
                  ? `${selectedCampus.name} campus`
                  : "Select a campus below to see its address."}
              </p>
            </div>

            <ul className="contact-list">
              <li>
                <span className="contact-list-icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <div>
                  <div className="contact-list-label">Address</div>
                  <div>
                    {selectedCampus?.address ||
                      "Select a campus in the form to see its address."}
                  </div>
                </div>
              </li>
              {selectedCampus && (
                <li>
                  <span className="contact-list-icon">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </span>
                  <div>
                    <div className="contact-list-label">Phone</div>
                    <a href={`tel:${selectedCampus.phone}`}>
                      {selectedCampus.phone}
                    </a>
                  </div>
                </li>
              )}
            </ul>

            <button
              type="button"
              className="btn btn-primary w-100 mt-2"
              onClick={() => setShowContact(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <section className="app-hero">
        <div className="container">
          <h2 className="app-hero-title">Student Registration</h2>
          <p className="app-hero-sub">
            Parents — please complete this form to register your child for
            admission at Metropolitan Academy.
          </p>
        </div>
      </section>

      <main className="container app-main">
        {confirmedRegistrationNumber ? (
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="card text-center p-4">
                <div className="mb-3">
                  <svg
                    width="56"
                    height="56"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#198754"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </div>
                <h3 className="mb-2">Registration Submitted</h3>
                <p className="text-muted mb-3">
                  Thank you — we've received your registration.{" "}
                  {selectedCampus?.name} Campus will contact you with the
                  next steps.
                </p>
                <div className="card bg-light border-0 p-3 mb-3">
                  <div className="small text-muted">
                    Your Registration Number
                  </div>
                  <div className="fs-4 fw-bold">
                    {confirmedRegistrationNumber}
                  </div>
                </div>
                <p className="small text-muted mb-4">
                  Please save this number for your records — you'll need it
                  if you contact the campus about this registration.
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRegisterAnother}
                >
                  Register Another Student
                </button>
              </div>
            </div>
          </div>
        ) : (
          <RegistrationForm
            key={formKey}
            onSubmitted={handleSubmitted}
            onCampusChange={setSelectedCampusId}
          />
        )}
      </main>

      <footer className="app-footer">
        <div className="container">
          <p className="mb-0">
            &copy; {new Date().getFullYear()} Metropolitan Academy. All rights
            reserved.
          </p>
          {selectedCampus && (
            <p className="mb-0 small text-muted">{selectedCampus.address}</p>
          )}
        </div>
      </footer>

      <ToastContainer position="top-right" autoClose={4000} />
    </div>
  );
};

export default App;
